import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLabel } from '@platform/hooks/useLabel';
import { useWorkspaceParams } from '@platform/hooks/useWorkspaceParams';
import { routes } from '@platform/core/lib/routes';
import { TextCommit } from '@platform/features/workspaces/builder';
import { usePlatformCatalogQuery } from '@platform/state/api';
import { ModuleTile } from '@platform/ui';
import { SysTable } from '../../system/SysTable';
import { SystemPage } from '../../system/SystemPage';
import { useCustomizationScope } from '../../system/useCustomizationScope';
import { ModuleSwitchCell } from '../ModuleSwitchCell';
import { typeRowText } from '../typeRowText';
import { useWorkspaceDefinitions } from '../useWorkspaceDefinitions';
import { globalTypeRows } from './globalTypeRows';
import { publishedTypeName } from './publishedTypeName';
import { useGlobalModuleActions } from './useGlobalModuleActions';
import '../customize.css';

const COLUMNS = 'minmax(200px, 1.8fr) minmax(110px, 1fr) minmax(150px, 1.2fr) minmax(130px, 1fr) 160px 230px';

/**
 * «Каталог модулів» рівня «Глобально» (прототип v4 pgCustMods при cscope==='global', ADR-027): рядок — кожен шаблон каталогу (169
 * реалізованих типів записів у порядку TEMPLATES) з плиткою й категорією шаблону; назва редагується на місці для всіх робочих просторів
 * без власної назви, «Увімкнено» — для всіх просторів («Вимкнено всюди»), «Записи й форми» — глобальне визначення модуля, «Скинути» —
 * глобальні назва, доступність і форма. «Відкрити» й «+ Додати модуль» на цьому рівні немає. Змінюють адміністратор платформи й
 * налаштувальник; доки стан каталогу не завантажено — лише перегляд.
 */
export function GlobalCatalogPage() {
  const { t } = useTranslation();
  const label = useLabel();
  const { ws } = useWorkspaceParams();
  const scope = useCustomizationScope(ws);
  const { definitions } = useWorkspaceDefinitions(ws, true);
  const { data: platform } = usePlatformCatalogQuery(undefined, { skip: !scope.canGlobal });
  const actions = useGlobalModuleActions();
  const canEdit = scope.canGlobal && !!platform;
  const rows = globalTypeRows(definitions ?? [], platform, label, publishedTypeName).map((s) => {
    const key = s.row.def.module.key;
    const types = typeRowText(s.row, label, t);
    const forms = typeRowText(s.row, label, t, true);
    return {
      key: `${key}/${s.row.rt.key}`,
      cells: [
        <span key="n" className="sp-who"><ModuleTile icon={s.tile.icon} tone={s.tile.tone} size={28} />
          <TextCommit className="cu-modname" value={s.name} label={t('sysCust.modules.name')} title={t('sysCust.modules.rename')} disabled={!canEdit} onCommit={(v) => actions.rename(s, v)} /></span>,
        <span key="c" className="sp-muted">{s.category}</span>,
        <span key="t" title={types.tip}>{types.text}</span>,
        <span key="f" title={forms.tip}>{forms.text}</span>,
        <ModuleSwitchCell key="s" enabled={s.enabled} canEdit={canEdit} required={s.required} hostName={s.hostName} offNote={t('sysCust.catalog.off')}
          onToggle={(on) => actions.toggle(s, on)} />,
        <span key="a" className="sp-actions">
          <Link className="sp-btn sp-btn--accent" to={routes.customizeModule(key, s.hostName ? s.row.rt.key : undefined)}>{t('sysCust.modules.recordsForms')}</Link>
          {canEdit && s.canReset && (
            <button type="button" className="sp-btn sp-btn--danger" title={t('sysCust.modules.resetTip')} aria-label={t('sysCust.modules.resetTip')}
              onClick={() => actions.reset(s)}>{t('sysCust.modules.reset')}</button>
          )}
        </span>,
      ],
    };
  });
  const head = [t('sysCust.modules.module'), t('sysCust.modules.category'), t('sysCust.modules.types'), t('sysCust.modules.forms'), t('sysCust.modules.enabled'), ''];
  return (
    <SystemPage title={t('sysPages.sections.catalog')} sub={t('sysCust.modules.subGlobal')}>
      <div className="cu-modrows"><SysTable columns={COLUMNS} minWidth={1000} head={head} rows={rows} label={t('sysPages.sections.catalog')} /></div>
    </SystemPage>
  );
}
